/**
 * Round-related types
 *
 * This module uses discriminated unions to support game-specific round settings.
 * Each game type has its own settings interface with a `gameType` discriminator.
 *
 * Prefixes:
 * - Hide and Seek: "Hns"
 * - FP Race: "Fpr"
 * - Hide and Seek Teams: "Hnst"
 * - Both Hide and Seek game types: "HideSeek"
 */
import { GameType } from '../constants/gameTypes';
export const DEFAULT_HNS_SEEKER_VISIBILITY = 'admins';
/**
 * How far (metres, combat-log units) a Marco Polo position may be moved before
 * players see it. The real spot is always inside a circle of this radius around
 * the shown one.
 */
export const DEFAULT_HNS_MARCO_POLO_RADIUS = 50;
export const HNS_MARCO_POLO_RADIUS_MIN = 10;
export const HNS_MARCO_POLO_RADIUS_MAX = 500;
/** A Hide and Seek round's location and position settings when none are chosen. */
export const DEFAULT_HNS_LOCATION_SETTINGS = {
    location: null,
    marcoPoloRadius: DEFAULT_HNS_MARCO_POLO_RADIUS,
    seekerVisibility: DEFAULT_HNS_SEEKER_VISIBILITY,
};
/** The most locations a Hide and Seek game may plan (each one's maps are prefetched). */
export const HNS_LOCATION_PLAN_MAX = 10;
/**
 * The parts of a Hide and Seek round the rules page can show or keep secret.
 * `scoring` is Hide and Seek Teams only and `seekerVisibility` Hide and Seek only.
 * Hiding the planet keeps the area secret too.
 */
export const HIDE_SEEK_PUBLIC_DISPLAY_KEYS = [
    'planet',
    'area',
    'durations',
    'hiderRules',
    'seekerRules',
    'marcoPolo',
    'camera',
    'scoring',
    'seekerVisibility',
    'timeline',
];
export const HIDE_SEEK_PUBLIC_DISPLAY_LABELS = {
    planet: 'Planet',
    area: 'Area and map',
    durations: 'Hiding and round time',
    hiderRules: 'Rules for hiders',
    seekerRules: 'Rules for seekers',
    marcoPolo: 'Marco Polo accuracy',
    camera: 'Max camera distance',
    scoring: 'Scoring',
    seekerVisibility: 'Who sees seekers',
    timeline: 'Marco Polo times and rule changes',
};
export const HIDE_SEEK_PUBLIC_NOTES_MAX = 500;
/** The display keys a game type's rounds have, in order. */
export function hideSeekPublicDisplayKeys(gameType) {
    const notFor = gameType === GameType.HIDE_AND_SEEK_TEAMS ? 'seekerVisibility' : 'scoring';
    return HIDE_SEEK_PUBLIC_DISPLAY_KEYS.filter((key) => key !== notFor);
}
/** A round's display settings with every part filled in: anything not set is shown. */
export function resolveHideSeekPublicDisplay(stored) {
    const display = {};
    for (const key of HIDE_SEEK_PUBLIC_DISPLAY_KEYS)
        display[key] = stored?.[key] !== false;
    if (!display.planet)
        display.area = false;
    return display;
}
export const DEFAULT_HNST_DEATH_OUTCOME = 'disqualified';
export const HNST_DEATH_OUTCOME_LABELS = {
    disqualified: 'Disqualified',
    eliminated: 'Eliminated',
};
/** What a hider's death over the Deaths limit does in this round. */
export function getHnstDeathOutcome(settings) {
    return settings.deathOutcome ?? DEFAULT_HNST_DEATH_OUTCOME;
}
export const HNST_SECONDS_PER_FIND_DEFAULT = 60;
export const HNST_SECONDS_PER_FIND_MIN = 1;
export const HNST_SECONDS_PER_FIND_MAX = 600;
export const DEFAULT_HNST_ROUND_SCORING = { mode: 'mean' };
/** A Hide and Seek Teams round's scoring, Mean-based when none was chosen. */
export function getHnstRoundScoring(settings) {
    return settings.scoring ?? DEFAULT_HNST_ROUND_SCORING;
}
/** A Hide and Seek Teams round's location and position settings when none are chosen. */
export const DEFAULT_HNST_LOCATION_SETTINGS = {
    location: null,
    marcoPoloRadius: DEFAULT_HNS_MARCO_POLO_RADIUS,
};
/**
 * Default FP Race round settings
 */
export const DEFAULT_FPR_SETTINGS = {
    gameType: GameType.FP_RACE,
};
/** Default base points for the first team to capture. */
export const DEFAULT_TRL_BASE_POINTS = 1000;
/** Default points decrement per rank. */
export const DEFAULT_TRL_POINTS_DECREMENT = 100;
/**
 * Default Trailblazers round settings (imageId must be supplied by the admin)
 */
export const DEFAULT_TRL_SETTINGS = {
    gameType: GameType.TRAILBLAZERS,
    searchDuration: 600,
    basePoints: DEFAULT_TRL_BASE_POINTS,
    pointsDecrement: DEFAULT_TRL_POINTS_DECREMENT,
};
// ============================================================================
// Type Guards
// ============================================================================
/**
 * Type guard to check if settings are Hide and Seek settings
 */
export function isHnsSettings(settings) {
    return settings.gameType === GameType.HIDE_AND_SEEK;
}
/**
 * Type guard to check if settings are Hide and Seek Teams settings
 */
export function isHnstSettings(settings) {
    return settings.gameType === GameType.HIDE_AND_SEEK_TEAMS;
}
/**
 * Type guard for the settings of either Hide and Seek game type (Hide and
 * Seek, Hide and Seek Teams): for what both play the same way — timers, role
 * rules, Marco Polo, locations.
 */
export function isHideSeekSettings(settings) {
    return isHnsSettings(settings) || isHnstSettings(settings);
}
/**
 * Type guard to check if settings are FP Race settings
 */
export function isFprSettings(settings) {
    return settings.gameType === GameType.FP_RACE;
}
/**
 * Type guard to check if settings are Trailblazers settings
 */
export function isTrlSettings(settings) {
    return settings.gameType === GameType.TRAILBLAZERS;
}
// ============================================================================
// Utility Functions
// ============================================================================
/**
 * Get the rules applicable to a specific role in Hide and Seek
 */
export function getHnsRulesForRole(settings, role) {
    return role === 'hider' ? settings.hiderRules : settings.seekerRules;
}
/**
 * Helper to check if a round is in an active state
 * For Hide and Seek: 'hiding' or 'seeking'
 * For FP Race: 'countdown' or 'racing'
 */
export function isRoundActive(round) {
    if (!round)
        return false;
    // Hide and Seek active states
    if (round.status === 'hiding' || round.status === 'seeking')
        return true;
    // FP Race active states
    if (round.status === 'countdown' || round.status === 'racing')
        return true;
    // Trailblazers active state
    if (round.status === 'searching')
        return true;
    return false;
}
/**
 * Check if a Hide and Seek round is in the hiding phase
 */
export function isHnsRoundHiding(round) {
    return round !== null && round !== undefined && round.status === 'hiding';
}
/**
 * Check if a Hide and Seek round is in the seeking phase
 */
export function isHnsRoundSeeking(round) {
    return round !== null && round !== undefined && round.status === 'seeking';
}
